// The account area's shared pieces: the page head with its way back, and
// the signed-in customer's profile at this restaurant. The identity is the
// device's anonymous account (auth.ts); the profile behind it is the
// phone-keyed customer row it claimed, null until it claims one.

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fetchMe, type Customer } from '../../lib/api';

export const accountPath = (rid: string, sid: string, page = '') => `/r/${rid}/s/${sid}/account${page ? `/${page}` : ''}`;

export function AccountHead({ back, backLabel, kicker, title }: { back: string; backLabel: string; kicker?: string; title: string }) {
  return (
    <header className="co-head">
      <Link to={back} className="co-backbtn" aria-label={backLabel}>‹</Link>
      <div>
        {kicker && <div className="co-store">{kicker}</div>}
        <h1>{title}</h1>
      </div>
    </header>
  );
}

/** The profile at this restaurant: undefined while loading, null before one is claimed. */
export function useMe(rid: string) {
  const [me, setMe] = useState<Customer | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  // bumped after a verified phone: the device may now speak for another identity
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    fetchMe(rid).then(
      (m) => live && setMe(m),
      (e) => live && setError(e instanceof Error ? e.message : 'Could not load your profile.'),
    );
    return () => { live = false; };
  }, [rid, tick]);
  return { me, setMe, error, reload: () => setTick((t) => t + 1) };
}

/** Shown where a page needs the profile and there is none yet. */
export function NeedsProfile({ rid, sid, children }: { rid: string; sid: string; children: ReactNode }) {
  return (
    <section className="co-sec" data-section="needs-profile">
      <p className="acct-quiet">{children}</p>
      <Link to={accountPath(rid, sid, 'profile')} className="co-back">Set up your profile →</Link>
    </section>
  );
}
